<script setup lang="ts">
import type { InteractionPreviewProps } from '../../../../model/assessments/preview-interactions.hook'

import { AppText } from '@shared/ui/typography'

import { usePreviewFillInBlanks } from '../../../../model/assessments/preview-interactions.hook'
import PreviewAuthorNote from '../PreviewAuthorNote.vue'

/**
 * The sentence with each `{{}}` rendered as an empty gap. Interpolated as text — there is no
 * `v-html` anywhere in this feature — and the marker comes from the schema's `GAP_MARKER`, so it
 * cannot drift from the editor's hints. Answers are shown per gap in an author note.
 */
defineOptions({
  name: 'PreviewFillInBlanks',
})

const props = defineProps<InteractionPreviewProps>()

const {
  refSegments,
  refGapCount,
  refBlanks,
  refHasText,
} = usePreviewFillInBlanks(() => props.interaction)
</script>

<template>
  <div class="preview-fill-in-blanks">
    <AppText
      v-if="!refHasText"
      name="body2"
      color="slate-500"
    >
      No text yet — a kid would see an empty sentence.
    </AppText>

    <p
      v-else
      class="preview-fill-in-blanks__text"
    >
      <template
        v-for="segment in refSegments"
        :key="segment.key"
      >
        <span v-if="segment.kind === 'text'">{{ segment.value }}</span>
        <span
          v-else
          class="preview-fill-in-blanks__gap"
          role="img"
          :title="`Gap ${segment.position}`"
          :aria-label="`Gap ${segment.position}`"
        />
      </template>
    </p>

    <AppText
      v-if="refHasText && refGapCount === 0"
      name="body2"
      color="slate-500"
    >
      This text has no gaps, so there is nothing for a kid to fill in.
    </AppText>

    <PreviewAuthorNote v-if="refBlanks.length > 0">
      answers
      <ul class="preview-fill-in-blanks__keys">
        <li
          v-for="blank in refBlanks"
          :key="blank.key"
        >
          <span>Gap {{ blank.position }}: {{ blank.answerLabel }}</span>
        </li>
      </ul>
    </PreviewAuthorNote>
  </div>
</template>

<style scoped lang="scss" src="./PreviewFillInBlanks.scss" />
